import { useState } from "react";
import { Button } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";

import { BucketNumberEnum } from "features/trading/buckets";
import { BucketActions } from "./useBucketActions";
import { BucketInceptionDateModal } from "./BucketInceptionDateModal";

interface BucketCreateButtonProps {
  number: BucketNumberEnum;
  accountId: number | null | undefined;
  actions: Pick<BucketActions, "create" | "isCreating">;
  sx?: SxProps<Theme>;
}

/**
 * "Create Bucket N", and the inception-date prompt that creating goes
 * through. Shared by the two places a bucket can be created — the client
 * page's Buckets tab and the report settings' bucket popup — so both ask
 * the same question and neither can create a bucket without one.
 *
 * The date is gathered BEFORE the bucket exists and travels in the create
 * request, rather than being PATCHed onto a bucket that was already made:
 * that is what lets Cancel mean "no bucket", and it leaves no window in
 * which a half-created bucket sits there with no date.
 */
export const BucketCreateButton = ({
  number,
  accountId,
  actions,
  sx,
}: BucketCreateButtonProps) => {
  const [isPromptOpen, setIsPromptOpen] = useState(false);

  const handleCreate = async (inceptionDate: string | null) => {
    // The prompt cannot produce null in create mode (no Clear button), and
    // a bucket is never created without a date — so nothing to create from.
    if (!inceptionDate) return;
    const ok = await actions.create(inceptionDate);
    if (ok) setIsPromptOpen(false);
  };

  return (
    <>
      <Button
        variant="contained"
        sx={sx}
        disabled={!accountId || actions.isCreating}
        onClick={() => setIsPromptOpen(true)}
      >
        Create Bucket {number}
      </Button>
      <BucketInceptionDateModal
        open={isPromptOpen}
        mode="create"
        number={number}
        inceptionDate={null}
        saving={actions.isCreating}
        onClose={() => setIsPromptOpen(false)}
        onSave={handleCreate}
      />
    </>
  );
};
